import type { AgentSummary, MastraEvent, UserMessage, AssistantMessage, ChatMessage } from './types';
import { createRun } from './run-state';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function messageText(message: UnknownRecord): string {
  if (typeof message.content === 'string') return message.content;
  const content = isRecord(message.content) ? message.content : {};
  if (typeof content.content === 'string') return content.content;
  const parts = Array.isArray(content.parts) ? content.parts : [];
  return parts
    .map((part) => isRecord(part) && typeof part.text === 'string' ? part.text : '')
    .join('');
}

export async function fetchAgents(apiBase: string, signal?: AbortSignal): Promise<AgentSummary[]> {
  const response = await fetch(`${apiBase}/api/agents`, { signal });
  if (!response.ok) throw new Error(`Agent registry returned ${response.status}`);
  const data: unknown = await response.json();
  if (!isRecord(data)) return [];

  return Object.entries(data).map(([id, value]) => {
    const agent = isRecord(value) ? value : {};
    const tools = isRecord(agent.tools) ? Object.keys(agent.tools) : [];
    return {
      id,
      name: typeof agent.name === 'string' ? agent.name : id,
      description: typeof agent.description === 'string' ? agent.description : undefined,
      tools,
    };
  });
}

export async function fetchThreadMessages(
  apiBase: string,
  threadId: string,
  agentId: string,
  signal?: AbortSignal,
): Promise<ChatMessage[]> {
  const response = await fetch(`${apiBase}/api/memory/threads/${threadId}/messages`, { signal });
  if (response.status === 404) return [];
  if (!response.ok) throw new Error(`Thread history returned ${response.status}`);
  const data: unknown = await response.json();
  const root = isRecord(data) ? data : {};
  const messages = Array.isArray(root.messages) ? root.messages : Array.isArray(data) ? data : [];

  return messages.flatMap((value): ChatMessage[] => {
    if (!isRecord(value)) return [];
    const text = messageText(value);
    if (!text) return [];
    const createdAt = typeof value.createdAt === 'string'
      ? Date.parse(value.createdAt)
      : typeof value.createdAt === 'number' ? value.createdAt : Date.now();
    const id = typeof value.id === 'string' ? value.id : crypto.randomUUID();
    if (value.role === 'user') {
      return [{ id, role: 'user', text, createdAt } satisfies UserMessage];
    }
    const run = createRun({
      id: `restored-${id}`,
      agentId,
      mode: agentId === 'lead' ? 'network' : 'stream',
      prompt: '',
      threadId,
      startedAt: createdAt,
    });
    run.status = 'restored';
    run.answer = text;
    run.endedAt = createdAt;
    run.trace = [];
    return [{ id, role: 'assistant', createdAt, run } satisfies AssistantMessage];
  });
}

export async function streamAgentRun(input: {
  apiBase: string;
  agentId: string;
  message: string;
  threadId: string;
  resourceId: string;
  signal: AbortSignal;
  onEvent: (event: MastraEvent) => void;
}): Promise<void> {
  const mode = input.agentId === 'lead' ? 'network' : 'stream';
  const response = await fetch(`${input.apiBase}/api/agents/${input.agentId}/${mode}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      messages: [{ role: 'user', content: input.message }],
      memory: { thread: input.threadId, resource: input.resourceId },
      ...(mode === 'network' ? { maxSteps: 8 } : {}),
    }),
    signal: input.signal,
  });

  if (!response.ok) {
    const detail = (await response.text()).slice(0, 500);
    throw new Error(`${response.status} ${response.statusText}${detail ? ` — ${detail}` : ''}`);
  }
  if (!response.body) throw new Error('Mastra returned an empty response stream');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) return;
    const data = trimmed.slice(5).trim();
    if (!data || data === '[DONE]') return;
    try {
      const event: unknown = JSON.parse(data);
      if (isRecord(event) && typeof event.type === 'string') input.onEvent(event as MastraEvent);
    } catch {
      // A malformed telemetry line should not discard the remainder of a valid stream.
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf('\n');
    while (newline >= 0) {
      processLine(buffer.slice(0, newline));
      buffer = buffer.slice(newline + 1);
      newline = buffer.indexOf('\n');
    }
  }

  buffer += decoder.decode();
  if (buffer.trim()) processLine(buffer);
}
